import React, { useMemo, useState } from 'react';
import CabCrudPage from './CabCrudPage';
import useCabLookups from './useCabLookups';

const AdminCabsPackageVariants = () => {
  const { vehicleCategories, seatConfigs, transportPackages } = useCabLookups();
  const [packageFilter, setPackageFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const packageOptions = useMemo(
    () => transportPackages.map((pkg) => ({ value: pkg.id, label: pkg.title })),
    [transportPackages]
  );
  const categoryOptions = useMemo(
    () => vehicleCategories.map((c) => ({ value: c.id, label: c.name })),
    [vehicleCategories]
  );
  const seatOptions = useMemo(
    () => seatConfigs.map((c) => ({ value: c.id, label: `${c.name} (${c.seatCapacity} seats)` })),
    [seatConfigs]
  );

  const transformPayload = (payload) => ({
    ...payload,
    maxPax: payload.maxPax === '' ? null : Number(payload.maxPax),
    priceTotal: payload.priceTotal === '' ? null : Number(payload.priceTotal),
    kmsIncluded: payload.kmsIncluded === '' ? null : Number(payload.kmsIncluded),
    extraKmRate: payload.extraKmRate === '' ? null : Number(payload.extraKmRate),
    gstPct: payload.gstPct === '' ? null : Number(payload.gstPct),
    priority: payload.priority === '' ? 0 : Number(payload.priority),
  });

  return (
    <CabCrudPage
      title="Package Variants"
      description="Configure package variants by vehicle and seating."
      resource="package-variants"
      listParams={() => ({ packageId: packageFilter || undefined, vehicleCategoryId: categoryFilter || undefined })}
      listParamsDeps={[packageFilter, categoryFilter]}
      filterControls={(
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label text-sm">Filter by package</label>
            <select
              className="form-select"
              value={packageFilter}
              onChange={(e) => setPackageFilter(e.target.value)}
            >
              <option value="">All packages</option>
              {packageOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label text-sm">Filter by category</label>
            <select
              className="form-select"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All categories</option>
              {categoryOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      defaultItem={{
        packageId: '',
        vehicleCategoryId: '',
        seatConfigId: '',
        maxPax: '',
        priceTotal: '',
        kmsIncluded: '',
        extraKmRate: '',
        driverAllowanceIncluded: false,
        tollIncluded: false,
        gstPct: '',
        priority: 0,
        isActive: true,
      }}
      columns={[
        {
          key: 'package',
          label: 'Package',
          render: (row) => row.package?.title || '-',
        },
        {
          key: 'vehicleCategory',
          label: 'Category',
          render: (row) => row.vehicleCategory?.name || '-',
        },
        {
          key: 'seatConfig',
          label: 'Seat config',
          render: (row) => row.seatConfig?.name || '-',
        },
        { key: 'maxPax', label: 'Max pax' },
        { key: 'priceTotal', label: 'Price' },
        { key: 'priority', label: 'Priority' },
      ]}
      fields={[
        { name: 'packageId', label: 'Package', type: 'select', required: true, options: packageOptions },
        { name: 'vehicleCategoryId', label: 'Vehicle category', type: 'select', required: true, options: categoryOptions },
        { name: 'seatConfigId', label: 'Seat configuration', type: 'select', required: true, options: seatOptions },
        { name: 'maxPax', label: 'Max pax', type: 'number', required: true },
        { name: 'priceTotal', label: 'Price total', type: 'number', required: true },
        { name: 'kmsIncluded', label: 'Kms included', type: 'number' },
        { name: 'extraKmRate', label: 'Extra km rate', type: 'number' },
        { name: 'gstPct', label: 'GST percent', type: 'number' },
        { name: 'priority', label: 'Priority', type: 'number' },
        { name: 'driverAllowanceIncluded', label: 'Driver allowance included', type: 'checkbox', col: 'col-md-6' },
        { name: 'tollIncluded', label: 'Toll included', type: 'checkbox', col: 'col-md-6' },
        { name: 'isActive', label: 'Active', type: 'checkbox', col: 'col-md-6' },
      ]}
      transformPayload={transformPayload}
    />
  );
};

export default AdminCabsPackageVariants;
